import { filterRange, filterSelects } from '../../utils/scan';
import { scanCategoryData } from '../scan/ScannerCategory';

/**
 * ScanResultController - Functional module for combining scan criteria data
 *
 * Merges data from different scan types (Exchange, SecurityType, etc.)
 * and returns a consistent format for the ScanResultsTable component.
 */

const typeMap = {
  0: 'range',
  2: 'select',
  3: 'multiselect'
};

/**
 * Combines scan results from multiple criteria into a single dataset
 *
 * @param {Array} scanTypes - Array of scan types to include ['exchange', 'securityType']
 * @param {number} maxResults - Maximum number of results to return (default: 100)
 * @returns {Array} Combined scan results with merged displayValues
 */

// Should we return all results to the table by default? and let the table
// paginate? For now, we limit to 100 here.

const MAX_RESULTS_DEFAULT = 100;

function normalizeFields(scanTypesOrFields) {
  if (!Array.isArray(scanTypesOrFields) || scanTypesOrFields.length === 0) {
    return [];
  }

  // If the caller passed actual scan fields (from selectedScan.fields), use them.
  if (typeof scanTypesOrFields[0] === 'object') {
    return scanTypesOrFields;
  }

  // Otherwise, accept an array of scan type strings and create minimal field objects.
  return scanTypesOrFields
    .filter(Boolean)
    .map((scanType) => {
      const category = scanCategoryData?.[scanType];
      const inferredType = category ? typeMap[category.Input] : undefined;
      return {
        id: scanType,
        type: inferredType,
        values: []
      };
    });
}

function buildSymbolDataForField(field, scanData) {
  const key = field?.id?.toLowerCase();
  const symbols = scanData?.[key]?.symbols;
  if (!symbols) {
    return [];
  }

  return Object.keys(symbols).map((symbol) => ({
    symbol,
    data: symbols[symbol]
  }));
}

function applyFieldFilter(symbolData, field, previousResults) {
  if (!field?.id || !scanCategoryData?.[field.id]) {
    // Unknown field id: don't filter.
    return symbolData;
  }

  if (field.type === 'range') {
    return filterRange(symbolData, field, scanCategoryData, previousResults);
  }

  if (field.type === 'select' || field.type === 'multiselect') {
    return filterSelects(symbolData, field, scanCategoryData, previousResults);
  }

  // Unrecognized type: don't filter.
  return symbolData;
}

/**
 * Computes the scan results table rows by:
 * - Taking the base symbol set from the first enabled field (or the first field if all disabled)
 * - Applying enabled field filters as intersections
 * - Merging displayValues across all fields for the resulting symbols
 *
 * Supports either:
 * - Passing `fields` (preferred): [{ id, type, values }, ...]
 * - Or passing `scanTypes`: ['Exchange', 'SecurityType', ...] (creates minimal field objects)
 */
export const getCombinedScanResults = (
  scanData = {},
  enabledScanCriteria = {},
  fieldsOverride = null
) => {
  const fields = normalizeFields(fieldsOverride);
  if (!fields || fields.length === 0) {
    return [];
  }

  // Build symbolData per field once
  const symbolDataByFieldId = fields.reduce((acc, field) => {
    acc[field.id] = buildSymbolDataForField(field, scanData);
    return acc;
  }, {});

  const enabledFields = fields.filter(
    (field) => enabledScanCriteria[field.id] !== false
  );

  const baseField = enabledFields[0] || fields[0];
  if (!baseField?.id) {
    return [];
  }

  let filtered = symbolDataByFieldId[baseField.id] || [];

  // If the base field is enabled, apply its own filter; otherwise keep full list.
  if (enabledScanCriteria[baseField.id] !== false) {
    filtered = applyFieldFilter(filtered, baseField, null);
  }

  let previousResults = filtered.reduce((acc, item) => {
    acc[item.symbol] = true;
    return acc;
  }, {});

  // Apply remaining enabled field filters as intersections
  enabledFields
    .filter((field) => field.id !== baseField.id)
    .forEach((field) => {
      const currentSymbolData = symbolDataByFieldId[field.id] || [];
      filtered = applyFieldFilter(currentSymbolData, field, previousResults);

      previousResults = filtered.reduce((acc, item) => {
        acc[item.symbol] = true;
        return acc;
      }, {});
    });

  // Merge displayValues for all fields (enabled or disabled) for table columns.
  return filtered.slice(0, MAX_RESULTS_DEFAULT).map((item) => {
    const mergedDisplayValues = fields.reduce((acc, field) => {
      const key = field.id.toLowerCase();
      const symbolEntry = scanData?.[key]?.symbols?.[item.symbol];
      const displayValues = symbolEntry?.displayValues;
      if (displayValues && typeof displayValues === 'object') {
        return Object.assign(acc, displayValues);
      }
      return acc;
    }, {});

    return {
      symbol: item.symbol,
      ...mergedDisplayValues
    };
  });
};

/**
 * Gets scan results for a specific scan type only
 *
 * @param {string} scanType - The scan type ('exchange', 'securityType')
 * @param {number} maxResults - Maximum number of results to return
 * @returns {Array} Scan results for the specified type
 */
// Default export object for backwards compatibility
const scanResultController = {
  getCombinedScanResults
};

export default scanResultController;
